import React from 'react';
import { Alert, Radio, Space, Typography, Tag } from 'antd';
import { RectificationPreview, RectificationOption } from '../types.ts';

const { Text } = Typography;

interface RectificationPreviewPanelProps {
  preview: RectificationPreview | null;
  loading?: boolean;
  /** Fase selezionata dall'utente (chiave: stage|shipment_id|customs_document_item_id) */
  selectedOptionKey?: string;
  onSelectOption?: (option: RectificationOption) => void;
  /** Scelta "bolletta rettificata dalla dogana" (solo fase disponibile con bolletta) */
  customsRectified?: boolean | null;
  onCustomsRectifiedChange?: (value: boolean) => void;
}

export const optionKey = (o: RectificationOption) =>
  `${o.stage}|${o.shipment_id ?? ''}|${o.customs_document_item_id ?? ''}`;

/**
 * Pannello condiviso dell'anteprima di rettifica quantità (motore backend
 * lot_rectification): mostra dove sta la merce della partita, lascia scegliere la
 * fase in cui applicare la differenza, elenca effetti, avvisi e blocchi.
 */
const RectificationPreviewPanel: React.FC<RectificationPreviewPanelProps> = ({
  preview,
  loading,
  selectedOptionKey,
  onSelectOption,
  customsRectified,
  onCustomsRectifiedChange
}) => {
  if (!preview) return null;

  const sign = preview.delta > 0 ? '+' : '';
  const options = preview.options || [];
  const currentKey = selectedOptionKey ?? (preview.option ? optionKey(preview.option) : undefined);
  const selected = options.find((o) => optionKey(o) === currentKey) || preview.option;
  const showCustomsChoice = !!selected && selected.stage === 'available' && !!selected.customs_document_item_id;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <div style={{ padding: 12, backgroundColor: '#fafafa', borderRadius: 4 }}>
        <Text strong>{preview.product_code}</Text>
        {preview.order_number ? <Text type="secondary"> — ordine fornitore {preview.order_number}</Text> : null}
        <br />
        <Text>
          Quantità: <strong>{preview.old_quantity}</strong> → <strong>{preview.new_quantity}</strong>{' '}
          <Tag color={preview.delta > 0 ? 'green' : 'volcano'}>{sign}{preview.delta} pz</Tag>
        </Text>
        {preview.detail_id ? (
          <>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Annullato {preview.quantity_cancelled} · assegnato a spedizioni {preview.shipped} · ancora da spedire {preview.residual_to_ship}
            </Text>
          </>
        ) : null}
      </div>

      {options.length > 0 && (
        <div>
          <Text strong>Dov'è la differenza?</Text>
          <Radio.Group
            style={{ display: 'block', marginTop: 8 }}
            value={currentKey}
            disabled={loading}
            onChange={(e) => {
              const opt = options.find((o) => optionKey(o) === e.target.value);
              if (opt && onSelectOption) onSelectOption(opt);
            }}
          >
            <Space direction="vertical">
              {options.map((o) => (
                <Radio key={optionKey(o)} value={optionKey(o)}>
                  <strong>{o.label}</strong>
                  {o.description ? ` — ${o.description}` : ''}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {' '}({o.qty} pz, prenotati {o.reserved}, liberi {o.free}{o.rows > 1 ? `, ${o.rows} righe` : ''})
                  </Text>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </div>
      )}

      {showCustomsChoice && (
        <div>
          <Text strong>La bolletta è stata rettificata dalla dogana?</Text>
          <Radio.Group
            style={{ display: 'block', marginTop: 8 }}
            value={customsRectified === null || customsRectified === undefined ? undefined : customsRectified}
            disabled={loading}
            onChange={(e) => onCustomsRectifiedChange && onCustomsRectifiedChange(e.target.value)}
          >
            <Space direction="vertical">
              <Radio value={true}>Sì: la quantità caricata sulla bolletta viene aggiornata</Radio>
              <Radio value={false}>No: la bolletta resta com'è, la differenza viene registrata come rettifica sulla giacenza (differenza inventariale)</Radio>
            </Space>
          </Radio.Group>
        </div>
      )}

      {preview.effects.length > 0 && (
        <Alert
          type="info"
          showIcon
          message="Effetti della rettifica"
          description={<ul style={{ margin: 0, paddingLeft: 18 }}>{preview.effects.map((e, i) => <li key={i}>{e}</li>)}</ul>}
        />
      )}
      {preview.warnings.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message="Attenzione"
          description={<ul style={{ margin: 0, paddingLeft: 18 }}>{preview.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>}
        />
      )}
      {preview.blocks.length > 0 && (
        <Alert
          type="error"
          showIcon
          message="Rettifica non applicabile"
          description={<ul style={{ margin: 0, paddingLeft: 18 }}>{preview.blocks.map((b, i) => <li key={i}>{b}</li>)}</ul>}
        />
      )}
    </Space>
  );
};

export default RectificationPreviewPanel;
